import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Star, Camera, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { ReviewLightbox } from './ProductReviews';
import ResponsiveImage from './ResponsiveImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_TILES = 15;

const resolveUrl = (u) => {
  if (!u) return u;
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

// Flach in Tiles pushen — pro Bewertung/Gästebuch-Eintrag alle Fotos zurückgeben,
// dedupiert wird später per `source_id`.
const flattenReviewPhotos = (items) => {
  const out = [];
  (items || []).forEach((r) => {
    (r.photos || []).forEach((p, i) => {
      out.push({
        id: `${r.id}-${i}`,
        source_id: `review-${r.id}`,
        url: p.url,
        rating: r.rating,
        display_name: r.display_name,
        product_name: r.product_name,
        text: r.text,
        title: r.title,
        verified: r.verified_customer || r.verified_booking,
        all_photos: r.photos,
        photo_index: i,
      });
    });
  });
  return out;
};

const flattenGuestbookPhotos = (entries) => {
  const out = [];
  (entries || []).forEach((e, ei) => {
    const urls = e.photo_urls || [];
    if (!urls.length) return;
    const productName = (e.product_refs && e.product_refs[0]?.name) || '';
    const photoObjs = urls.map((u) => ({ url: u }));
    urls.forEach((u, i) => {
      out.push({
        id: `gb-${e.id || ei}-${i}`,
        source_id: `gb-${e.id || ei}`,
        url: u,
        rating: null,
        display_name: e.display_name || '',
        product_name: productName,
        text: e.comment || '',
        title: '',
        verified: true,
        all_photos: photoObjs,
        photo_index: i,
        source: 'guestbook',
      });
    });
  });
  return out;
};

// Normalisiert eine URL für den Dedupe-Vergleich (ohne Backend-Prefix, ohne Query).
const normalizeUrlKey = (u) => {
  if (!u) return '';
  return String(u).replace(BACKEND_URL, '').split('?')[0].trim();
};

// Normalisiert den Kunden-Namen (kleinbuchstaben, ohne Leerzeichen) für den Dedupe.
// So verhindert es Duplikate, wenn dieselbe Person sowohl im Gästebuch als auch
// als Bewertung Fotos hochgeladen hat.
const normalizeNameKey = (n) => (n || '').toLowerCase().replace(/\s+/g, ' ').trim();

const CustomerPhotoGallery = () => {
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState({ photos: null, index: null });
  const scrollerRef = useRef(null);
  const [scrollState, setScrollState] = useState({ atStart: true, atEnd: true });

  useEffect(() => {
    let stop = false;
    Promise.all([
      axios.get(`${API}/review-gallery?min_rating=4&limit=24`).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/public/guestbook?limit=30`).catch(() => ({ data: { entries: [] } })),
    ])
      .then(([revRes, gbRes]) => {
        if (stop) return;
        const gbTiles = flattenGuestbookPhotos(gbRes.data?.entries || []);
        const reviewTiles = flattenReviewPhotos(revRes.data?.items || []);
        // Gästebuch zuerst (persönlicher, neuer), dann Reviews.
        const merged = [...gbTiles, ...reviewTiles];
        // 1) Pro Kunden-Name UND pro Source (falls Name fehlt) nur EIN Foto,
        //    zusätzlich URL-Dedupe für den seltenen Fall, dass zwei Kunden
        //    dasselbe Foto teilen.
        const seenName = new Set();
        const seenSource = new Set();
        const seenUrl = new Set();
        const deduped = [];
        for (const t of merged) {
          const nameKey = normalizeNameKey(t.display_name);
          if (nameKey && seenName.has(nameKey)) continue;
          if (t.source_id && seenSource.has(t.source_id)) continue;
          const urlKey = normalizeUrlKey(t.url);
          if (!urlKey || seenUrl.has(urlKey)) continue;
          if (nameKey) seenName.add(nameKey);
          seenSource.add(t.source_id);
          seenUrl.add(urlKey);
          deduped.push(t);
          if (deduped.length >= MAX_TILES) break;
        }
        setTiles(deduped);
      })
      .finally(() => { if (!stop) setLoading(false); });
    return () => { stop = true; };
  }, []);

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const atStart = el.scrollLeft <= 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setScrollState({ atStart, atEnd });
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [tiles.length]);

  const canScroll = useMemo(() => tiles.length > 4, [tiles.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cardWidth = el.querySelector('[data-gallery-tile]')?.clientWidth || 220;
    el.scrollBy({ left: (cardWidth + 12) * 2 * dir, behavior: 'smooth' });
  };

  if (loading) return null;
  if (!tiles.length) return null;

  const openTile = (tile) => setLightbox({ photos: tile.all_photos, index: tile.photo_index });
  const closeLb = () => setLightbox({ photos: null, index: null });
  const prevLb = () => setLightbox((cur) => cur.photos ? ({ ...cur, index: (cur.index - 1 + cur.photos.length) % cur.photos.length }) : cur);
  const nextLb = () => setLightbox((cur) => cur.photos ? ({ ...cur, index: (cur.index + 1) % cur.photos.length }) : cur);

  return (
    <section className="py-10 md:py-14" data-testid="customer-photo-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-10">
          <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-white bg-sky-700 px-3 py-1 rounded-full mb-3 shadow-sm">
            <Camera className="h-3.5 w-3.5" /> Live aus echten Events
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-sky-800">
            Foto-Galerie unserer Kunden
          </h2>
          <p className="text-base md:text-lg text-neutral-700 leading-relaxed max-w-2xl mx-auto mt-2">
            Authentische Eindrücke aus Kundenmomenten & Bewertungen — hochgeladen von echten Familien. Klick auf ein Foto für eine größere Ansicht.
          </p>
        </div>

        <div className="relative">
          {canScroll && (
            <>
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                disabled={scrollState.atStart}
                className="hidden md:inline-flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-white border border-sky-200 text-sky-700 shadow-md hover:bg-sky-50 disabled:opacity-40 disabled:cursor-not-allowed items-center justify-center transition"
                aria-label="Vorherige Fotos"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                disabled={scrollState.atEnd}
                className="hidden md:inline-flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-sky-600 hover:bg-sky-700 text-white shadow-md disabled:opacity-40 disabled:cursor-not-allowed items-center justify-center transition"
                aria-label="Weitere Fotos"
                data-testid="gallery-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          <div
            ref={scrollerRef}
            className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 lg:mx-0 lg:px-0 scroll-smooth"
            style={{ scrollbarWidth: 'none' }}
            data-testid="gallery-scroller"
          >
            <style>{`[data-testid="gallery-scroller"]::-webkit-scrollbar { display: none; }`}</style>
            {tiles.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => openTile(t)}
                data-gallery-tile
                className="snap-start shrink-0 w-[62%] sm:w-[240px] md:w-[220px] lg:w-[210px] group relative aspect-square rounded-2xl overflow-hidden border-2 border-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all bg-neutral-200"
                data-testid={`gallery-tile-${t.id}`}
              >
                <ResponsiveImage
                  src={t.url}
                  alt={`Foto von ${t.display_name}`}
                  sizes="(max-width: 640px) 62vw, (max-width: 1024px) 240px, 210px"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-2 flex items-end justify-between gap-1 pointer-events-none">
                  {t.rating ? (
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`h-3 w-3 ${n <= (t.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-white/30'}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white/95 bg-sky-500/70 rounded-full px-1.5 py-0.5">
                      <Camera className="h-2.5 w-2.5" /> Kundenmoment
                    </span>
                  )}
                  {t.verified && (
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" aria-label="Verifiziert" />
                  )}
                </div>
                <div className="absolute inset-x-0 top-0 bg-gradient-to-b from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <p className="text-[10px] font-bold text-white truncate">{t.display_name}</p>
                  {t.product_name && (
                    <p className="text-[9px] text-white/80 truncate">{t.product_name}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
      <ReviewLightbox
        photos={lightbox.photos}
        index={lightbox.index}
        onClose={closeLb}
        onPrev={prevLb}
        onNext={nextLb}
      />
    </section>
  );
};

export default CustomerPhotoGallery;
